import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Avatar,
  Fab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BuildIcon from "@mui/icons-material/Build";
import {
  Save,
  Publish,
  Edit,
  Delete,
  Visibility,
  Add,
  Work,
  People,
  AttachMoney,
  Schedule,
  Close,
  Drafts,
  BookmarkBorder,
} from "@mui/icons-material";
import apiEndpoints from "../../apiconfig";
import { useNavigate } from "react-router-dom";
import "./managejobs.css";

const CreateEditJob = ({ showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [skillsList, setSkillsList] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [skillsError, setSkillsError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteJob, setPendingDeleteJob] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const navigate = useNavigate();

  // Hardcoded data
  const jobData = {
    // categories: [
    //   { id: "1", name: "Web Development" },
    //   { id: "2", name: "Mobile Development" },
    //   { id: "3", name: "ui/ux Design" },
    //   { id: "4", name: "Data Science" },
    //   { id: "5", name: "Content Writing" },
    //   { id: "6", name: "Digital Marketing" },
    //   { id: "7", name: "Graphic Design" },
    //   { id: "8", name: " Software Development" },
    // ],
    experienceLevels: [
      { id: "1", name: "Beginner" }, // Removed extra spaces
      { id: "2", name: "Intermediate" },
      { id: "3", name: "Expert" },
    ],
    projectTypes: ["Fixed Price", "Hourly Rate"],
  };

  // Helper functions
  const getCategoryName = (id) => {
    // First handle if id is an object (might come from API directly)
    if (id && typeof id === "object" && id.domain_name) {
      return id.domain_name;
    }

    // Then check if it's a string/number ID and we have categories loaded
    if (categories && categories.length > 0) {
      const category = categories.find((c) => c.id == id); // Use loose equality
      return category ? category.domain_name : "Unknown";
    }

    return "Unknown";
  };

  const getPublishedStatus = (status) => {
    return status === true || status === "1" || status === 1;
  };

  const getExperienceName = (id) => {
    // console.log("Looking up experience ID:", id, "Type:", typeof id);
    // console.log("Available experience levels:", jobData.experienceLevels);

    if (!id) return "Unknown";

    const idStr = String(id).trim();
    const exp = jobData.experienceLevels.find((e) => {
      // console.log("Comparing:", String(e.id).trim(), "with", idStr);
      return String(e.id).trim() === idStr;
    });

    const result = exp ? exp.name : "Unknown";
    // console.log("Result:", result);
    return result;
  };
  // console.log("jobData", jobData);
  const getSkillNames = (skillIds) => {
    if (!skillIds) return [];

    try {
      // Handle string (JSON), array, or already parsed array
      let ids = [];
      if (typeof skillIds === "string") {
        // Remove any escaping if present
        const cleanString = skillIds.replace(/\\/g, "");
        ids = JSON.parse(cleanString);
      } else if (Array.isArray(skillIds)) {
        ids = skillIds;
      }

      // Ensure we have an array of numbers (not strings)
      ids = ids.map((id) => Number(id));

      return ids.map((id) => {
        const skill = skillsList.find((s) => Number(s.id) === id);
        return skill ? skill.skill_name : "Unknown";
      });
    } catch (e) {
      console.error("Error parsing skill IDs:", e);
      return [];
    }
  };

  const [formData, setFormData] = useState({
    jobTitle: "",
    jobDescription: "",
    domain_id: "",
    experience_id: "",
    projectType: "",
    budget: "",
    projectDuration: "",
    durationValue: 1,
    durationUnit: "months",
    skill_id: [],
    additionalInformations: "",
    isPublished: false,
  });

  const [errors, setErrors] = useState({});

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiEndpoints.jobcrud, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }

      const data = await response.json();
      setJobs(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      showSnackbar("Failed to load jobs", "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const fetchSkills = async () => {
      try {
        setLoadingSkills(true);
        const response = await fetch(apiEndpoints.skilldropdown, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch skills");
        }

        const data = await response.json();
        setSkillsList(data);
        setLoadingSkills(false);
      } catch (error) {
        console.error("Error fetching skills:", error);
        setSkillsError(error.message);
        setLoadingSkills(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await fetch(apiEndpoints.domain_dropdown, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }

        const data = await response.json();
        setCategories(data.data || data); // Adjust based on your API response structure
        setLoadingCategories(false);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setLoadingCategories(false);
      }
    };

    fetchCategories();

    fetchSkills();
    fetchJobs();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      const job = jobs.find((j) => j.job_guid === id);
      if (job) {
        // Parse existing duration if available
        const durationParts = job.projectDuration
          ? job.projectDuration.split(" ")
          : ["1", "months"];

        setFormData({
          jobTitle: job.jobTitle,
          jobDescription: job.jobDescription,
          domain_id: job.domain_id,
          experience_id: job.experience_id,
          projectType: job.projectType,
          budget: job.budget,
          projectDuration: job.projectDuration,
          durationValue: parseInt(durationParts[0]) || 1,
          durationUnit: durationParts.length > 1 ? durationParts[1] : "months",
          skill_id: Array.isArray(job.skill_id)
            ? job.skill_id.map((id) => Number(id))
            : typeof job.skill_id === "string"
              ? JSON.parse(job.skill_id).map((id) => Number(id))
              : [],
          additionalInformations: job.additionalInformations,
          isPublished: Boolean(job.isPublished),
        });
        setEditingJob(job);
        setOpenModal(true);
      }
    }
  }, [isEditing, id, jobs]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job title is required";
    if (!formData.jobDescription.trim())
      newErrors.jobDescription = "Job description is required";
    if (!formData.domain_id) newErrors.domain_id = "Category is required";
    if (!formData.experience_id)
      newErrors.experience_id = "Experience level is required";
    if (!formData.projectType)
      newErrors.projectType = "Project type is required";
    if (!formData.budget || formData.budget <= 0)
      newErrors.budget = "Valid budget is required";
    if (
      !formData.projectDuration &&
      (!formData.durationValue || Number(formData.durationValue) < 1)
    )
      newErrors.projectDuration = "Duration must be at least 1";
    if (formData.skill_id.length === 0)
      newErrors.skill_id = "At least one skill is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    setLoading(true);

    // Combine duration fields for display
    const durationText = `${formData.durationValue} ${formData.durationUnit}`;

    const submitData = {
      ...formData,
      projectDuration: durationText,
      isPublished: isDraft ? false : true,
      skill_id: formData.skill_id,
    };

    if (validateForm()) {
      try {
        const token = sessionStorage.getItem("token");
        const url = editingJob
          ? `${apiEndpoints.jobcrud}?job_guid=${editingJob.job_guid}`
          : apiEndpoints.jobcrud;

        const method = editingJob ? "PUT" : "POST";

        const response = await fetch(url, {
          method: method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(submitData),
        });

        if (!response.ok) {
          throw new Error(
            editingJob ? "Failed to update job" : "Failed to create job"
          );
        }
        fetchJobs();
        const data = await response.json();

        showSnackbar(
          editingJob
            ? `Job updated and ${isDraft ? "saved as draft" : "published"
            } successfully!`
            : `Job created and ${isDraft ? "saved as draft" : "published"
            } successfully!`,
          "success"
        );

        handleCloseModal();
      } catch (error) {
        console.error("Error saving job:", error);
        showSnackbar(
          error.message || "Failed to save job. Please try again.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    const durationParts = job.projectDuration
      ? job.projectDuration.split(" ")
      : ["1", "months"];

    setFormData({
      jobTitle: job.jobTitle,
      jobDescription: job.jobDescription,
      domain_id: job.domain_id,
      experience_id: job.experience_id,
      projectType: job.projectType,
      budget: job.budget,
      projectDuration: job.projectDuration,
      durationValue: parseInt(durationParts[0]) || 1,
      durationUnit: durationParts.length > 1 ? durationParts[1] : "months",
      skill_id: Array.isArray(job.skill_id)
        ? job.skill_id
        : typeof job.skill_id === "string"
          ? JSON.parse(job.skill_id)
          : [],
      additionalInformations: job.additionalInformations,
      isPublished: getPublishedStatus(job.isPublished),
    });
    setOpenModal(true);
  };

  const confirmDeleteJob = (job) => {
    setPendingDeleteJob(job);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDeleteJob?.job_guid) return;

    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiEndpoints.jobcrud}?job_guid=${pendingDeleteJob.job_guid}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete job");
      }
      fetchJobs();
      showSnackbar("Job deleted successfully!", "success");
      setDeleteConfirmOpen(false);
      setPendingDeleteJob(null);
    } catch (error) {
      console.error("Error deleting job:", error);
      showSnackbar(error.message || "Failed to delete job", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingJob(null);
    setFormData({
      jobTitle: "",
      jobDescription: "",
      domain_id: "",
      experience_id: "",
      projectType: "",
      budget: "",
      projectDuration: "",
      durationValue: 1,
      durationUnit: "months",
      skill_id: [],
      additionalInformations: "",
      isPublished: false,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleViewJob = (job) => {
    setViewingJob(job);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingJob(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingJob(null);
    setFormData({
      jobTitle: "",
      jobDescription: "",
      domain_id: "",
      experience_id: "",
      projectType: "",
      budget: "",
      projectDuration: "",
      durationValue: 1,
      durationUnit: "months",
      skill_id: [],
      additionalInformations: "",
      isPublished: false,
    });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "durationValue") {
      const parsed = parseInt(String(value), 10);
      nextValue = Number.isNaN(parsed) ? 1 : Math.max(1, parsed);
    }

    if (name === "budget") {
      if (value === "") {
        nextValue = "";
      } else {
        const parsed = Number(value);
        nextValue = Number.isNaN(parsed) ? "" : Math.max(0, parsed);
      }
    }

    setFormData({
      ...formData,
      [name]: nextValue,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const preventNegativeNumberInput = (event) => {
    if (["-", "+", "e", "E"].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleViewProposals = (jobGuid) => {
    navigate(`/proposals/${jobGuid}`);
  };

  const handleSkillsChange = (event) => {
    const { value } = event.target;
    const selectedSkills = (
      Array.isArray(value) ? value : [value].filter(Boolean)
    ).map((id) => Number(id));

    setFormData({
      ...formData,
      skill_id: selectedSkills,
    });
    if (errors.skill_id) {
      setErrors({
        ...errors,
        skill_id: "",
      });
    }
  };

  const JobCard = ({ job, onViewProposals }) => {
    const isPublished = getPublishedStatus(job.isPublished);

    const getSkillNames = (skillIds) => {
      if (!skillIds) return [];

      try {
        const ids =
          typeof skillIds === "string"
            ? JSON.parse(skillIds.replace(/\\/g, ""))
            : skillIds;

        return ids.map((id) => {
          const numId = Number(id);
          const skill = skillsList.find((s) => Number(s.id) === numId);
          return skill ? skill.skill_name : "Unknown";
        });
      } catch (e) {
        console.error("Error parsing skill IDs:", e);
        return [];
      }
    };

    const jobSkills = getSkillNames(job.skill_id || []).filter(
      (skill) => skill !== "Unknown"
    );

    const getCategoryName = (job) => {
      if (
        job.domain_id &&
        typeof job.domain_id === "object" &&
        job.domain_id.domain_name
      ) {
        return job.domain_id.domain_name;
      }

      if (job.domain_name) {
        return job.domain_name;
      }

      const category = categories.find((c) => c.id == job.domain_id);
      return category ? category.domain_name : "Unknown";
    };

    return (
      <div className="job-card">
        <div className="job-card-header">
          <div className="job-card-heading">
            <div className="job-category-label">{job.domain_name || getCategoryName(job)}</div>
            <div className="job-title" title={job.jobTitle}>
              {job.jobTitle}
            </div>

            <div className="job-badges-row">
              <span className={isPublished ? "badge-published" : "badge-unpublished"}>
                {isPublished ? "Published" : "Unpublished"}
              </span>
              <span className="job-category">
                {job.domain_name || getCategoryName(job)}
              </span>
            </div>
          </div>

          <div className="icon-buttons-group">
            <Tooltip title="View Job" arrow>
              <button
                className="icon-button-custom"
                onClick={() => handleViewJob(job)}
              >
                <Visibility sx={{ fontSize: "18px" }} />
              </button>
            </Tooltip>

            <Tooltip title="Edit Job" arrow>
              <button
                className="icon-button-custom icon-button-edit"
                onClick={() => handleEdit(job)}
              >
                <Edit sx={{ fontSize: "18px" }} />
              </button>
            </Tooltip>

            <Tooltip title="Delete Job" arrow>
              <button
                className="icon-button-custom icon-button-delete"
                onClick={() => confirmDeleteJob(job)}
              >
                <Delete sx={{ fontSize: "18px" }} />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="job-description">
          {job.jobDescription}
        </div>

        <div className="skill-tags-container">
          {jobSkills.slice(0, 3).map((skill, index) => (
            <span key={index} className="skill-tag">
              {skill}
            </span>
          ))}
          {jobSkills.length > 3 && (
            <span className="skill-tag">
              +{jobSkills.length - 3}
            </span>
          )}
        </div>

        <div className="job-stats-row">
          <div className="job-stat-box">
            <div className="job-stat-label">Budget</div>
            <div className="job-stat-value">₹{job.budget}</div>
          </div>
          <div className="job-stat-box">
            <div className="job-stat-label">Duration</div>
            <div className="job-stat-value">{job.projectDuration || "-"}</div>
          </div>
        </div>

        <div className="job-footer">
          <div className="job-price-container">
            <span className="job-price">
              ₹{job.budget}
            </span>
            <span className="job-price-unit">
              {job.projectType === "Hourly Rate" ? "/hr" : ""}
            </span>
          </div>

          <button
            className="view-proposals"
            onClick={() => onViewProposals(job.job_guid)}
          >
            View Proposals
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="manage-jobs-container">
      <div className="manage-jobs-header">
        <div>
          <h1 className="manage-jobs-title">Manage Jobs</h1>
          <p className="manage-jobs-subtitle">Create, edit, and manage your job postings</p>
        </div>

        <button className="add-job-button" onClick={handleCreateNew}>
          <Add />
        </button>
      </div>

      <div className="jobs-grid-container">
        <div className="jobs-grid">
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onViewProposals={handleViewProposals}
              />
            ))
          ) : (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "64px 0" }}>
              <Typography variant="body1" color="text.secondary">
                No job postings found. Create your first job to get started!
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Job Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "20px",
            maxHeight: "90vh",
            background: "#FFFFFF",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            width: "100%",
            margin: isMobile ? "12px" : "32px",
          },
        }}
      >
        {/* Header with flat background */}
        <DialogTitle
          style={{
            padding: "20px 24px",
            background: "#0F172A",
            color: "white",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {editingJob ? "✏️ Edit Job" : "✨ Create New Job"}
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              style={{ color: "white" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? "20px" : "32px", pt: isMobile ? "30px" : "48px" }}>
          <form onSubmit={(e) => handleSubmit(e, false)}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: "20px",
                width: "100%",
                mt: { xs: 1, sm: 1.5 },
              }}
            >
              <TextField
                fullWidth
                name="jobTitle"
                label="Job Title"
                value={formData.jobTitle}
                onChange={handleChange}
                error={!!errors.jobTitle}
                helperText={errors.jobTitle}
                placeholder="e.g., Senior React Developer"
              />

              <TextField
                fullWidth
                select
                name="domain_id"
                label="Category"
                value={formData.domain_id}
                onChange={handleChange}
                error={!!errors.domain_id}
                helperText={errors.domain_id}
                disabled={loadingCategories}
              >
                {loadingCategories ? (
                  <MenuItem disabled>
                    <LinearProgress sx={{ width: "100%" }} />
                  </MenuItem>
                ) : (
                  categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.domain_name}
                    </MenuItem>
                  ))
                )}
              </TextField>

              <TextField
                fullWidth
                select
                name="experience_id"
                label="Experience Level"
                value={formData.experience_id}
                onChange={handleChange}
                error={!!errors.experience_id}
                helperText={errors.experience_id}
              >
                {jobData.experienceLevels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                select
                name="projectType"
                label="Project Type"
                value={formData.projectType}
                onChange={handleChange}
                error={!!errors.projectType}
                helperText={errors.projectType}
              >
                {jobData.projectTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                name="budget"
                label={formData.projectType === "Hourly Rate" ? "Hourly Rate (₹)" : "Budget (₹)"}
                type="number"
                value={formData.budget}
                onChange={handleChange}
                onKeyDown={preventNegativeNumberInput}
                error={!!errors.budget}
                helperText={errors.budget}
                placeholder={formData.projectType === "Hourly Rate" ? "500" : "5000"}
                inputProps={{ min: 0, step: "any" }}
              />

              <Box sx={{ display: "flex", gap: "12px", flexDirection: { xs: "column", sm: "row" } }}>
                <TextField
                  fullWidth
                  name="durationValue"
                  label="Duration"
                  type="number"
                  value={formData.durationValue}
                  onChange={handleChange}
                  error={!!errors.projectDuration}
                  helperText={errors.projectDuration}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  select
                  name="durationUnit"
                  label="Unit"
                  value={formData.durationUnit}
                  onChange={handleChange}
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </TextField>
              </Box>

              <FormControl
                fullWidth
                error={!!errors.skill_id}
                sx={{
                  gridColumn: { sm: "span 2" },
                  "& .MuiOutlinedInput-root": {
                    minHeight: 48,
                    alignItems: "center",
                  },
                  "& .MuiSelect-select": {
                    minHeight: "unset !important",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    py: "12px",
                  },
                }}
              >
                <InputLabel shrink>Required Skills</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  name="skill_id"
                  value={formData.skill_id}
                  onChange={handleSkillsChange}
                  input={<OutlinedInput notched label="Required Skills" />}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 220,
                        overflowY: "auto",
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selected.length === 0
                        ? "Select required skills"
                        : selected
                          .map((skillId) => {
                            const skill = skillsList.find((s) => Number(s.id) === Number(skillId));
                            return skill ? skill.skill_name : "Unknown";
                          })
                          .join(", ")}
                    </Typography>
                  )}
                >
                  {skillsList.map((skill) => (
                    <MenuItem key={skill.id} value={Number(skill.id)}>
                      {skill.skill_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                name="jobDescription"
                label="Job Description"
                multiline
                rows={4}
                value={formData.jobDescription}
                onChange={handleChange}
                error={!!errors.jobDescription}
                helperText={errors.jobDescription}
                sx={{
                  gridColumn: { sm: "1 / -1" },
                  "& .MuiInputBase-root": { width: "100%" },
                }}
              />

              <TextField
                fullWidth
                name="additionalInformations"
                label="Additional Requirements"
                multiline
                rows={4}
                value={formData.additionalInformations}
                onChange={handleChange}
                sx={{
                  gridColumn: { sm: "1 / -1" },
                  "& .MuiInputBase-root": { width: "100%" },
                }}
              />

              <FormControlLabel
                sx={{ gridColumn: { sm: "span 2" } }}
                control={
                  <Switch
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {formData.isPublished ? "Published" : "Draft Mode"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.isPublished ? "Visible to public" : "Only visible to you"}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: "14px 20px", sm: "16px 32px" },
            borderTop: "1px solid #E5E7EB",
            gap: "12px",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Button
            onClick={handleCloseModal}
            sx={{
              color: "#4B5563",
              textTransform: "none",
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={(e) => handleSubmit(e, !formData.isPublished)}
            sx={{
              backgroundColor: "#36656B",
              textTransform: "none",
              fontWeight: 700,
              px: 4,
              py: 1.2,
              width: { xs: "100%", sm: "auto" },
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px #2B4F54",
              "&:hover": {
                backgroundColor: "#2B4F54",
                boxShadow: "0 10px 15px -3px #2B4F54",
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : editingJob ? "Save Changes" : "Post Job"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Job Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteJob(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #E5E7EB" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: "#475569" }}>
            Are you sure you want to delete{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#0F172A" }}>
              {pendingDeleteJob?.jobTitle || "this job"}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: "12px 20px 16px", gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setPendingDeleteJob(null);
            }}
            sx={{
              color: "#4B5563",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "20px",
            maxHeight: "90vh",
            background: "#FFFFFF",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <DialogTitle
          style={{
            padding: "24px 24px 16px",
            borderBottom: "1px solid #e2e8f0",
            background: "#0F172A",
            color: "#fff",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Job Details
            </Typography>
            <IconButton
              onClick={handleCloseViewModal}
              size="small"
              sx={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: "0" }}>
          {viewingJob && (
            <Box>
              {/* Premium Hero Section */}
              <Box sx={{ p: { xs: 3, md: 4 }, backgroundColor: "white" }}>
                <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                  <Tooltip title={viewingJob.jobTitle} disableHoverListener={viewingJob.jobTitle.length < 40}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#0F172A",
                        mb: 2,
                        wordBreak: "break-word",
                        lineHeight: 1.2,
                        fontSize: { xs: "1.5rem", md: "2.125rem" }
                      }}
                    >
                      {viewingJob.jobTitle}
                    </Typography>
                  </Tooltip>
                </Box>

                <Box display="flex" gap={1.5} mb={4} flexWrap="wrap">
                  <Chip
                    label={getPublishedStatus(viewingJob.isPublished) ? "Active Listing" : "Draft Listing"}
                    sx={{
                      backgroundColor: getPublishedStatus(viewingJob.isPublished) ? "#DCFCE7" : "#F1F5F9",
                      color: getPublishedStatus(viewingJob.isPublished) ? "#166534" : "#475569",
                      fontWeight: 700,
                      borderRadius: "8px",
                      px: 1,
                      "& .MuiChip-label": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  />
                  <Chip
                    label={getCategoryName(viewingJob.domain_id)}
                    sx={{
                      backgroundColor: "#EEF2FF",
                      color: "#4338CA",
                      fontWeight: 700,
                      borderRadius: "8px",
                      px: 1,
                      "& .MuiChip-label": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  />
                </Box>

                <Grid container spacing={4}>
                  {/* Left Column: Key Stats */}
                  <Grid item xs={12} md={7}>
                    <Typography
                      variant="overline"
                      sx={{ color: "#64748B", fontWeight: 800, letterSpacing: "0.1em" }}
                    >
                      Project Description
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#334155",
                        mt: 2,
                        lineHeight: 1.8,
                        fontSize: "1rem",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {viewingJob.jobDescription}
                    </Typography>

                    {viewingJob.additionalInformations && (
                      <Box sx={{ mt: 4 }}>
                        <Typography
                          variant="overline"
                          sx={{ color: "#64748B", fontWeight: 800, letterSpacing: "0.1em" }}
                        >
                          Additional Requirements
                        </Typography>
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: "#F8FAFC",
                            borderRadius: "12px",
                            border: "1px dashed #CBD5E1"
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "#475569", lineHeight: 1.6, overflowWrap: "anywhere", wordBreak: "break-word" }}
                          >
                            {viewingJob.additionalInformations}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Right Column: Details Grid */}
                  <Grid item xs={12} md={5}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        backgroundColor: "#F8FAFC",
                        borderRadius: "16px",
                        border: "1px solid #E2E8F0"
                      }}
                    >
                      <Grid container spacing={3}>
                        {[
                          { label: "Experience", value: getExperienceName(viewingJob.experience_id), icon: <WorkOutlineIcon />, color: "#3B82F6" },
                          { label: "Project Type", value: viewingJob.projectType, icon: <CategoryIcon />, color: "#8B5CF6" },
                          { label: viewingJob.projectType === "Hourly Rate" ? "Hourly Rate" : "Fixed Budget", value: viewingJob.projectType === "Hourly Rate" ? `₹${viewingJob.budget}/hr` : `₹${viewingJob.budget}`, icon: <AttachMoneyIcon />, color: "#10B981" },
                          { label: "Duration", value: viewingJob.projectDuration, icon: <AccessTimeIcon />, color: "#F59E0B" },
                        ].map((item, idx) => (
                          <Grid item xs={6} key={idx}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {React.cloneElement(item.icon, { sx: { fontSize: 18, color: item.color } })}
                                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
                                  {item.label}
                                </Typography>
                              </Box>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: "#1E293B" }}>
                                {item.value}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      <Typography
                        variant="overline"
                        sx={{ color: "#64748B", fontWeight: 800, letterSpacing: "0.1em", display: "block", mb: 2 }}
                      >
                        Required Skills
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {getSkillNames(viewingJob.skill_id).map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            sx={{
                              backgroundColor: "white",
                              border: "1px solid #E2E8F0",
                              color: "#1E293B",
                              fontWeight: 600,
                              borderRadius: "6px"
                            }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: "20px 32px",
            borderTop: "1px solid #F1F5F9",
            backgroundColor: "#F8FAFC",
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
        >
          <Button
            onClick={handleCloseViewModal}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: "#1E293B",
              color: "white",
              px: 4,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#0F172A",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateEditJob;